import React from 'react';

import { TestIdProps } from '@tanium/coreui-utils';
import { ButtonBase } from '@tanium/react-button-base';
import styled, { css as emotionCss } from '@tanium/react-emotion-9';
import { Icon } from '@tanium/react-graphics';
import { css, defineVariant, LayoutStyleProps } from '@tanium/style-system';
import { ThemeSpace } from '@tanium/theme-data';

import { IconButtonVariant, VARIANT_INVERTED, VARIANT_PRIMARY } from './IconButtonVariant';
import {
  InvertedActiveText,
  InvertedDisabledText,
  InvertedHoverText,
  InvertedSelectedText,
  InvertedText,
  PrimaryActive,
  PrimaryDisabledText,
  PrimaryHover,
  PrimarySelectedText,
  PrimaryText,
  SubduedText,
  SubtleText,
} from './themeDefinitions';

const iconStyle = emotionCss({ display: 'block' });

export interface IconButtonProps
  extends TestIdProps,
    Pick<LayoutStyleProps, 'display'>,
    Omit<React.PropsWithoutRef<JSX.IntrinsicElements['button']>, 'css'> {
  /**
   * Whether the button should be disabled
   */
  disabled?: boolean;
  /**
   * The height of the icon
   */
  height: number;
  /**
   * The icon to use
   */
  icon: Icon;
  /**
   * The label to use for the `aria-label` attribute
   */
  label: string;
  /**
   * Whether the icon should appear to be selected/active
   */
  selected?: boolean;
  /**
   * The width of the icon
   */
  width: number;
  /**
   * Class to apply to the button. Use with caution.
   */
  className?: string;
  /**
   * Appearance of the icon button. Use `primary`, `subtle`, `subdued` or `inverted`. Defaults to `primary`.
   */
  variant?: IconButtonVariant;
  /**
   * Callback to invoke when the button is clicked
   */
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

const getSelectedColor = (variant: IconButtonVariant | undefined) => {
  switch (variant) {
    case VARIANT_INVERTED:
      return InvertedSelectedText;
    default:
      return PrimarySelectedText;
  }
};

const primaryVariantStyles = {
  color: PrimaryText,
  ':hover': {
    color: PrimaryHover,
  },
  ':active': {
    color: PrimaryActive,
  },
  ':disabled': {
    color: PrimaryDisabledText,
  },
};

const variantCss = defineVariant<IconButtonVariant>(
  {
    primary: primaryVariantStyles,
    subtle: {
      ...primaryVariantStyles,
      color: SubtleText,
    },
    subdued: {
      ...primaryVariantStyles,
      color: SubduedText,
      [':not(:hover):not(:active):not(:disabled)' as string]: {
        opacity: 0.7,
      },
    },
    inverted: {
      color: InvertedText,
      ':hover': {
        color: InvertedHoverText,
      },
      ':active': {
        color: InvertedActiveText,
      },
      ':disabled': {
        color: InvertedDisabledText,
      },
    },
  },
  { defaultVariant: VARIANT_PRIMARY },
);

const StyledButton = styled(ButtonBase, { shouldForwardProp: (p) => p !== 'selected' })<
  Pick<IconButtonProps, 'variant' | 'disabled' | 'selected'>
>(
  css((theme) => {
    const minorSpace = 'minor' in theme.space ? theme.space.minor[1] : 4;
    return {
      p: ThemeSpace.minor[1],
      m: `${-1 * minorSpace}px`,
      bg: 'transparent',
      border: 0,
    };
  }),
  ({ variant, selected }) => [
    variantCss(variant),
    selected && css({ color: getSelectedColor(variant) }),
  ],
);

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, label, height, width, icon: IconComponent, onClick, variant, ...props }, ref) => (
    <StyledButton
      innerRef={ref}
      aria-label={label}
      {...props}
      className={className}
      onClick={(e) => {
        if (!props.disabled) {
          onClick(e);
        }
      }}
      type="button"
      variant={variant}
    >
      <IconComponent height={height} width={width} className={iconStyle} focusable="false" />
    </StyledButton>
  ),
);

IconButton.displayName = 'IconButton';

export default IconButton;
